<template>
  <div class="price">
    <div class="bg-2 p-6 rounded-16">
      <snow-input
        v-model="form.name"
        id="name"
        label="Nome da oferta *"
        :rules="rules.name"
      />
      <snow-input
        v-model="form.description"
        class="mt-4 mb-7"
        id="description"
        label="Descrição no checkout *"
        type="textarea"
        rows="4"
        :rules="rules.description"
        info="Insira uma descrição para a sua página de pagamentos. Esta descrição aparecerá para seus compradores."
      />
      <h4 class="text-semibold">Origem das vendas</h4>
      <snow-input v-model="form.sck" class="mt-4 mb-6" id="sck" label="SCK" />
      <h4 class="text-semibold mb-4">Definir período da oferta</h4>
      <div class="row">
        <div class="col-lg-6">
          <snow-date-picker
            isHours
            v-model="form.start_at"
            size="lg"
            format="YYYY-MM-DD HH:mm"
          />
        </div>
        <div class="col-lg-6">
          <snow-date-picker
            isHours
            v-model="form.end_at"
            size="lg"
            format="YYYY-MM-DD HH:mm"
          />
        </div>
      </div>
      <snow-input
        v-model="form.expire_url"
        id="expire_url"
        class="mt-2 mt-lg-4"
        label="URL para redirecionar após oferta expirar"
      />
      <snow-switch
        v-model="form.is_stock"
        class="mt-4"
        label="Ativar/Desativar estoque"
      />
      <div v-if="form.is_stock">
        <bp-input
          v-model="form.stock"
          class="mt-6 mt-lg-5"
          id="stock"
          label="Quantidade em estoque"
          type="tel"
          mask="##########"
          placeholder="0"
          :errors="errors.stock"
        />
        <bp-input
          v-model="form.stock_empty_url"
          class="mt-6 mt-lg-5"
          id="stock_empty_url"
          label="URL para redirecionar após estoque acabar"
          placeholder="Ex: http://www.meusite.com.br/estoque-acabou"
          :errors="errors.stock_empty_url"
        />
      </div>
      <h4 class="text-semibold mt-7">Precificação da oferta</h4>
      <snow-input
        v-model="form.price"
        label="Valor em Real Brasileiro (R$)"
        money
        class="my-5"
        id="price"
        required
        :rules="rules.price"
      />
      <snow-input
        v-model="form.price_usd"
        label="Valor em Dólar Americano (US$)"
        :disabled="!showForeign"
        :info="getInfo('USD')"
        class="my-5"
        id="price"
        money
        :moneyConfig="{ ...moneyConfig, prefix: 'US$ ' }"
        :rules="rules.foreignPricing"
      />
      <snow-input
        v-model="form.price_eur"
        label="Valor em Euro (€)"
        :disabled="!showForeign"
        :info="getInfo('EUR')"
        class="my-5"
        id="price"
        money
        :moneyConfig="{ ...moneyConfig, prefix: '€ ' }"
        :rules="rules.foreignPricing"
      />
      <h4 class="text-semibold mt-7">Configurar Order Bump</h4>
      <action-dispatcher
        class="my-4"
        action="product/get"
        :parameters="{ limit: 1000 }"
      >
        <div class="d-flex flex-column gap-4">
          <order-bump-add
            v-for="(order_bump, i) in form.order_bumps"
            :counter="i + 1"
            :form="order_bump"
            :key="i"
            @remove="item => form.order_bumps.splice(item - 1, 1)"
          />
        </div>
      </action-dispatcher>
      <button class="add-order-bump" @click="form.order_bumps.push({})">
        <div class="d-flex align-items-center text-dark">
          <i class="ph ph-plus mr-4" />
          <span>Novo Order Bump</span>
        </div>
      </button>
      <div class="d-flex w-100 justify-content-end">
        <snow-button
          :disabled="!isValid"
          @click="saveOffer"
          text="Salvar oferta"
          class="mt-9"
        />
      </div>
    </div>

    <div class="my-5 bg-2 p-6 rounded-16">
      <offers-or-plans-container
        :isSignature="false"
        :items="offers"
        @delete="i => onRemove(i)"
        @removeOrderBump="removeOrderBump"
        @edit="onEdit"
      />
    </div>
  </div>
</template>

<script>
import OffersOrPlansContainer from "@/components/product/OffersOrPlansContainer";
import OrderBumpAdd from "@/components/product/order-bump/OrderBumpAdd";
import SnowSwitch from "@/components/snow-ui/SnowSwitch";
import { formFields, toComputerDate } from "@/functions/helpers.js";
import { maxLength, moreOrEqualTo, required } from "@/functions/validation";
import moment from "moment";
import { mapState } from "vuex";
const fields = [
  "id",
  "name",
  "description",
  "price",
  "currency",
  "is_main",
  "status",
  "start_at",
  "end_at",
  "order_bump",
  "sck",
  "expire_url",
  "is_stock",
  "stock",
  "stock_empty_url",
  "order_bumps"
];

export default {
  components: {
    OffersOrPlansContainer,
    OrderBumpAdd,
    SnowSwitch
  },
  props: {
    productCurrency: {
      type: String,
      default: ""
    },
    priceToEditIndex: {
      type: Number,
      default: null
    },
    currentProduct: {
      type: Object,
      default: () => {}
    },
    formErrors: {
      type: Object,
      default: () => ({})
    },
    showLoading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    let offer;
    return {
      loading: false,
      offer,
      countOrderBumps: 1,
      modalDeleteVisible: false,
      moneyConfig: {
        decimal: ",",
        thousands: ".",
        precision: 2,
        prefix: "",
        masked: false
      },
      offers: [],
      ...formFields(fields, {
        order_bumps: [{}],
        status: "active",
        currency: "BRL",
        is_main: false, // to control the submit of default and other offers
        is_stock: false,
        ...offer
      })
    };
  },
  watch: {
    offers(newValue) {
      this.$emit("hasOffer", !!newValue.length);
    }
  },
  computed: {
    ...mapState("product", ["products", "paymentMethods"]),
    ...mapState("offer", ["orderBumpOffers"]),
    ...mapState("sale", ["installments"]),
    isValid() {
      return (
        this.form.name &&
        this.form.description &&
        this.form.description.length < 980 &&
        this.form.start_at &&
        this.form.price &&
        this.form.price >= 4 &&
        (!parseFloat(this.form.price_eur) || this.form.price_eur >= 4) &&
        (!parseFloat(this.form.price_usd) || this.form.price_usd >= 4) &&
        this.form.currency &&
        !this.form.order_bumps.some(
          order_bump => !!order_bump.product_id && !order_bump.offer_id
        )
        // && this.form.payment_methods_ids.length
      );
    },
    showForeign() {
      return this.currentProduct?.payment_methods_ids?.includes(1);
    },
    rules() {
      return {
        name: [required(), maxLength(64)],
        description: [required(), maxLength(980)],
        price: [required(), moreOrEqualTo(4)],
        foreignPricing: [
          val =>
            !!(!val || val >= 4) ||
            "Caso haja valor, deve ser maior ou igual a 4"
        ]
      };
    }
  },
  methods: {
    getInfo(currency) {
      if (this.showForeign) return "";
      const currencyName = {
        USD: "dólar americano",
        EUR: "euro"
      };
      return `Para habilitar configuração de preço em ${currencyName[currency]} seu checkout precisa aceitar Cartão de Crédito.`;
    },
    saveOffer() {
      let form = {
        ...this.form,
        status: "active",
        start_at: this.form.start_at
          ? toComputerDate(this.form.start_at)
          : null,
        end_at: this.form.end_at ? toComputerDate(this.form.end_at) : null,
        order_bumps: this.form.order_bumps.filter(ob => !!ob.offer_id)
      };

      if (Number.isInteger(this.indexToEdit)) {
        const aux = [...this.offers];

        aux.splice(this.indexToEdit, 1, form);
        this.offers = aux;
      } else {
        const existOffer = this.offers.find(p => p.name == this.form.name);

        if (existOffer) {
          this.errors.name = ["Já existe uma oferta com esse nome"];
          window.scrollTo(0, 0);
          return;
        }
        if (this.form.price < 4) {
          this.errors.price = ["O valor não pode ser menor que R$ 4,00"];
          return;
        }

        if (!this.orderBumpProduct) {
          form.order_bump = null;
        }
        this.offers = [...this.offers, form];
      }
      this.clear();
    },
    transformDate(date) {
      return moment(date, "DD/MM/YYYY hh:mm").toDate();
    },
    clear() {
      const { form, errors } = formFields(fields, {
        currency: "BRL",
        status: "active",
        installments: 1,
        is_main: false, // to control the submit of default and other offers
        is_stock: false,
        order_bumps: [{}]
      });
      this.form = form;
      this.errors = errors;
      this.indexToEdit = null;
    },
    onEdit(index) {
      this.form = {
        ...this.offers[index],
        start_at: moment(this.offers[index].start_at).format("DD/MM/YYYY hh:mm")
      };
      if (this.form.end_at) {
        this.form.end_at = moment(this.form.end_at).format("DD/MM/YYYY hh:mm");
      }
      this.indexToEdit = index;
      window.scrollTo({
        top: 0,
        behavior: "smooth"
      });
    },
    onRemove(index) {
      this.offers = this.offers.filter((_, i) => i !== index);
      this.clear();
    },
    removeOrderBump(index, orderBumpIndex) {
      this.offers[index].order_bumps.splice(orderBumpIndex, 1);
    }
  }
};
</script>

<style lang="scss" scoped>
.price {
  .add-order-bump {
    display: flex;
    align-self: stretch;
    align-items: center;
    flex-direction: column;
    justify-content: center;

    height: 68px;
    padding: 24px;
    width: 100%;
    border-radius: 16px;
    background: transparent;
    border: 1px dashed var(--black-20, rgba(0, 0, 0, 0.2));
  }

  .custom-control.custom-switch {
    z-index: 0 !important;
  }
}
</style>
